/**
 * Scan display values — the code→label/unit rendering shared by the AI Scanner.
 *
 * Extracted from `summarizePayload` (#719) when candidate selection (#736) grew
 * its own need for them: the seam emits each candidate's matched scan values in
 * the same `{ scan, label, value }` shape the summarize request uses, and
 * importing them straight from `summarizePayload` would have closed an import
 * cycle (`summarizePayload` → `scanCandidates` → `summarizePayload`).
 *
 * Pure — `localize` is passed in rather than hooked, so every function here is
 * unit-testable without a React tree or a store.
 *
 * The contract rules these enforce, for both a hashtag THRESHOLD and a symbol's
 * MATCHED value:
 *  - every rendered value is a STRING — Syphon 400s on a number
 *  - no raw Syphon codes go over the wire (`166,0111` → `東証プライム`)
 *  - an absent or unmappable value is `null`, never `0`/`"N/A"`/a raw key, so
 *    callers OMIT the entry rather than assert something untrue
 *
 * @see src/constants/summarizeDisplayMap.js for the code→label/unit table.
 */

import SCAN_DISPLAY from '../constants/summarizeDisplayMap';
import Logger from './logger';

/**
 * `localize` returns the key itself when a string is missing, which would put a
 * raw key like `scan.criteria.Sector.9999` into the prompt. Treat that as
 * "no label" so callers can omit rather than send garbage.
 *
 * An empty or whitespace-only translation is treated the same way. That does
 * collapse two different situations — a key that is absent, and a key that
 * exists but is blank — but both are equally unusable in the prompt, and the
 * caller's only option either way is to omit the entry.
 *
 * @param {Function} localize - `useLocalize()`'s localize.
 * @param {string} key - localization key.
 * @returns {string|null} the trimmed string, or null when `localize` or `key`
 *   is missing, `localize` throws, the value is not a string, the key resolves
 *   to itself (not found), or the value is empty/whitespace-only.
 */
export function localizeOrNull(localize, key) {
  if (!localize || !key) return null;
  let text;
  try {
    text = localize(key);
  } catch (e) {
    return null;
  }
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (trimmed.length === 0 || trimmed === key) return null;
  return trimmed;
}

/**
 * Formats a number to the scan's configured precision and unit.
 *
 * Trailing zeros are trimmed so a threshold of `15` reads `15%` rather than
 * `15.0%`, while a real value keeps its precision (`3.94%`).
 *
 * @returns {string|null} null when the value is not a finite number.
 */
function formatNumeric(scanName, raw, config, matched) {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) {
    // Logged only for a SYMBOL's value, where the input is Syphon data and a
    // non-numeric `filterValue` on a numeric scan means something upstream is
    // wrong. Thresholds come from our own config and are covered by the
    // operator sweep in tests, so warning on those would only add noise.
    if (matched) {
      Logger.warn({
        errorCode: 'E0047',
        message: `Scan summary: ${scanName} value is not a finite number: ${JSON.stringify(
          raw
        )}`
      });
    }
    return null;
  }
  // A zero that encodes "no data" is an absent value, not a real one. Gated on
  // `matched` so it only ever applies to a SYMBOL's value: a zero in a hashtag
  // threshold is a deliberate rule (`>= 0`), and dropping that would silently
  // remove a stated reason from `conditions[]`.
  if (matched && n === 0 && config.zeroIsAbsent) return null;

  const unit =
    matched && typeof config.matchedUnit === 'string'
      ? config.matchedUnit
      : config.unit || '';

  const decimals = typeof config.decimals === 'number' ? config.decimals : 2;

  // `Intl` does the rounding, the trailing-zero trim and the thousands
  // separators in one pass. Deliberately NOT regex-based: a trailing-zero strip
  // (`/\.?0+$/`) and a grouping lookahead (`/\B(?=(\d{3})+(?!\d))/`) both
  // backtrack quadratically on long digit runs (SonarQube S5852).
  //
  // `maximumFractionDigits` with no minimum is exactly the wanted behaviour:
  // a threshold of `15` reads `15`, while a real value keeps its precision
  // (`9,619.02`). Locale is pinned so grouping never varies by environment.
  const text = n.toLocaleString('en-US', {
    maximumFractionDigits: decimals,
    useGrouping: Boolean(config.grouped)
  });

  return `${text}${unit}`;
}

/**
 * Resolves a single CODE to its display label.
 *
 * `Exchange` is the awkward one: its `filterValue` is the compound
 * `<Listed_Exchange>,<SectionClassCode>` (e.g. `166,0111`), so the section is
 * taken from after the comma and resolved with the `.long` suffix — the plain
 * key is the `東P` short form, and the summary wants `東証プライム`.
 *
 * @returns {string|null} null when the code has no label (caller omits it).
 */
function formatCode(raw, config) {
  if (raw === null || raw === undefined) return null;
  let code = String(raw).trim();
  if (code.length === 0) return null;

  if (config.compound) {
    const parts = code.split(',');
    code = parts[parts.length - 1].trim();
  }

  // Scans whose string table is keyed by group, not by raw code.
  if (config.codeGroups || config.codeGroupDefault) {
    code =
      (config.codeGroups && config.codeGroups[code]) ||
      config.codeGroupDefault ||
      code;
  }

  return `${config.optionPrefix}${code}${config.optionSuffix || ''}`;
}

/**
 * Converts one raw scan value into its display representation.
 *
 * @param {boolean} [matched] - true when formatting a symbol's actual value
 *   rather than a hashtag threshold; selects `matchedUnit` where one is set.
 * @returns {string|null} null when absent or unmappable — callers OMIT rather
 *   than sending a placeholder (AC C19978731).
 */
export function formatScanValue(scanName, raw, localize, matched) {
  const config = SCAN_DISPLAY[scanName];
  if (!config) return null;
  if (raw === null || raw === undefined || raw === '') return null;

  if (config.optionPrefix) {
    const key = formatCode(raw, config);
    return key ? localizeOrNull(localize, key) : null;
  }

  return formatNumeric(scanName, raw, config, matched);
}

/** JP metric name for a scan, e.g. `MarketCap` → `時価総額`. */
export function scanLabel(scanName, localize) {
  const config = SCAN_DISPLAY[scanName];
  if (!config) return null;
  return localizeOrNull(localize, config.labelKey);
}
